// Employee master — reference data, the API <-> screen mapping, and the maths
// the screen shows (document expiry, pay totals, gratuity).
//
// The backend contract lives in docs/BACKEND-COMPLETE.md (Part 4.3). The server
// answers camelCase (`toDraft` reads it) and takes PascalCase (`toPayload`
// writes it), the same convention every other master follows. Keep the keys in
// those two functions in step with that document.

/* ------------------------------------------------------------ dates */

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const today = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};
export const iso = (d) =>
  d
    ? d.getFullYear() +
      "-" +
      String(d.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(d.getDate()).padStart(2, "0")
    : "";
// accepts "2026-09-23", "2026-09-23T00:00:00" or a Date
export const parseISO = (s) => {
  if (!s) return null;
  if (s instanceof Date) return new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const p = String(s).slice(0, 10).split("-");
  if (p.length !== 3 || !+p[0]) return null;
  return new Date(+p[0], +p[1] - 1, +p[2]);
};
export const isoOf = (s) => iso(parseISO(s));
export const addDays = (d, n) => {
  const x = new Date(d.getTime());
  x.setDate(x.getDate() + n);
  return x;
};
export const daysBetween = (a, b) => Math.round((b - a) / 86400000);
export const daysTo = (s) => {
  const d = parseISO(s);
  return d ? daysBetween(today(), d) : null;
};
export const yearsBetween = (a, b) => (b - a) / (365.25 * 86400000);
export const fmtDate = (s) => {
  const d = parseISO(s);
  return d ? d.getDate() + " " + MONTHS[d.getMonth()] + " " + String(d.getFullYear()).slice(2) : "—";
};
export const money = (n) =>
  (n < 0 ? "-" : "") + Math.abs(Math.round(+n || 0)).toLocaleString("en-US");
export const pct = (n, d) => (d ? (n / d) * 100 : 0);

/* ------------------------------------------------- reference defaults */
// Used until api/Employee/GetLookups answers. The server list wins field by
// field (see mergeLookups), so an ERP that keeps its own departments or
// document types overrides these without a frontend change.

export const DEFAULT_LOOKUPS = {
  currency: "QAR",
  departments: ["Sales", "Warehouse", "Accounts", "Purchase", "Administration", "IT", "Logistics", "Housekeeping"],
  designations: [
    "Store Manager", "Cashier", "Sales Executive", "Van Salesman", "Merchandiser", "Storekeeper",
    "Accountant", "Purchase Officer", "Driver", "Helper", "HR Officer", "IT Support", "Supervisor",
  ],
  grades: ["G1", "G2", "G3", "G4", "G5"],
  nationalities: [
    "Qatari", "Indian", "Filipino", "Nepali", "Bangladeshi", "Sri Lankan", "Egyptian",
    "Pakistani", "Sudanese", "Kenyan", "Jordanian", "Syrian",
  ],
  localNationalities: ["Qatari"],
  // applies: both | local | expat. validity in months; ladder = reminder days
  documentTypes: [
    { code: "PASSPORT", name: "Passport", applies: "both", mandatory: true, expires: true, validityMonths: 120, reminderDays: [180, 90, 60, 30] },
    { code: "QID", name: "Residence permit (QID)", applies: "expat", mandatory: true, expires: true, validityMonths: 12, reminderDays: [90, 60, 30, 15, 7] },
    { code: "NATID", name: "National ID", applies: "local", mandatory: true, expires: true, validityMonths: 60, reminderDays: [90, 60, 30] },
    { code: "VISA", name: "Entry / work visa", applies: "expat", mandatory: false, expires: true, validityMonths: 12, reminderDays: [90, 60, 30, 15, 7] },
    { code: "LABOUR", name: "Work permit / labour card", applies: "expat", mandatory: true, expires: true, validityMonths: 12, reminderDays: [90, 60, 30, 15, 7] },
    { code: "HEALTH", name: "Health card", applies: "both", mandatory: true, expires: true, validityMonths: 12, reminderDays: [60, 30, 15] },
    { code: "MEDICAL", name: "Medical fitness", applies: "expat", mandatory: false, expires: true, validityMonths: 24, reminderDays: [60, 30] },
    { code: "INSURE", name: "Insurance card", applies: "both", mandatory: false, expires: true, validityMonths: 12, reminderDays: [60, 30] },
    { code: "LICENCE", name: "Driving licence", applies: "both", mandatory: false, expires: true, validityMonths: 60, reminderDays: [90, 30] },
    { code: "CONTRACT", name: "Employment contract", applies: "both", mandatory: true, expires: false, validityMonths: 0, reminderDays: [] },
    { code: "EDU", name: "Educational certificate", applies: "both", mandatory: false, expires: false, validityMonths: 0, reminderDays: [] },
    { code: "PCC", name: "Police clearance", applies: "both", mandatory: false, expires: true, validityMonths: 36, reminderDays: [60] },
  ],
  // kind: Earning | Deduction | Employer cost
  salaryComponents: [
    { code: "BASIC", name: "Basic salary", kind: "Earning", gratuity: true, insurance: true },
    { code: "HRA", name: "Housing allowance", kind: "Earning", gratuity: false, insurance: true },
    { code: "TRANS", name: "Transport allowance", kind: "Earning", gratuity: false, insurance: false },
    { code: "FOOD", name: "Food allowance", kind: "Earning", gratuity: false, insurance: false },
    { code: "PHONE", name: "Phone allowance", kind: "Earning", gratuity: false, insurance: false },
    { code: "OTHER", name: "Other allowance", kind: "Earning", gratuity: false, insurance: false },
    { code: "SOCEMP", name: "Social insurance (employee)", kind: "Deduction", gratuity: false, insurance: false },
    { code: "LOAN", name: "Loan instalment", kind: "Deduction", gratuity: false, insurance: false },
    { code: "SOCCO", name: "Social insurance (employer)", kind: "Employer cost", gratuity: false, insurance: false },
  ],
  banks: ["Qatar National Bank", "Commercial Bank", "Doha Bank", "Qatar Islamic Bank", "Ahli Bank"],
  leavePolicies: ["Standard — Qatar", "Standard — Saudi Arabia", "Management"],
  leaveTypes: ["Annual", "Sick", "Emergency", "Unpaid", "Hajj / pilgrimage"],
  shifts: ["General 09:00–18:00", "Morning 07:00–15:00", "Evening 14:00–23:00", "Split"],
  commissionSchemes: ["Flat 1.5% of net sales", "Slab on target attainment", "Per product group"],
  priceLists: ["Retail", "Wholesale", "Corporate"],
  assetTypes: ["Mobile phone", "Laptop", "POS terminal", "Delivery van", "Scanner", "Uniform set", "Locker key", "SIM card"],
  tills: ["Till 1", "Till 2", "Till 3", "Van till"],
  targetMeasures: ["Net sales", "Gross profit", "Invoice count", "Collection", "New customers"],
};

export const STATUSES = ["Active", "On probation", "On leave", "Suspended", "Notice period", "Left"];
export const LEAVE_REASONS = ["Resignation", "Termination", "End of contract", "Retirement", "Absconding"];
export const RENEWAL_STAGES = ["Not started", "Documents collected", "Submitted", "With authority", "Collected"];

const nonEmpty = (v) => (Array.isArray(v) ? v.length > 0 : v != null && v !== "");
export const mergeLookups = (server) => {
  const out = { ...DEFAULT_LOOKUPS };
  if (server && typeof server === "object") {
    Object.keys(DEFAULT_LOOKUPS).forEach((k) => {
      if (nonEmpty(server[k])) out[k] = server[k];
    });
  }
  out.docBy = {};
  out.documentTypes.forEach((d) => {
    out.docBy[d.code] = d;
  });
  out.compBy = {};
  out.salaryComponents.forEach((c) => {
    out.compBy[c.code] = c;
  });
  return out;
};

export const isLocalNationality = (lk, nat) => (lk.localNationalities || []).indexOf(nat) > -1;

/* ------------------------------------------------- blank + mapping */

const defaultLeaves = (lk) =>
  (lk.leaveTypes || []).map((t) => ({
    t,
    opening: 0,
    accrued: t === "Annual" ? 21 : t === "Sick" ? 14 : t === "Emergency" ? 5 : t === "Unpaid" ? 30 : 10,
    taken: 0,
  }));

export const blankEmployee = (lk, branch) => ({
  id: 0,
  guid: "",
  name: "", arName: "", code: "", shortName: "", gender: "", dob: "", nationality: "",
  empType: "Expatriate", marital: "Single", blood: "",
  phone1: "", phone2: "", email: "", addr1: "", homeAddr: "", accom: "Company provided",
  emName: "", emRel: "", emPhone: "", deps: [],
  branch: branch?.guid || "", branches: branch?.guid ? [branch.guid] : [],
  dept: "", desig: "", grade: "", reportsTo: "", workLoc: "Branch",
  doj: iso(today()), contract: "Unlimited", contractEnd: "", probation: 6,
  status: "On probation", dol: "", leaveReason: "",
  docs: [],
  payFreq: "Monthly", payMode: "Bank transfer (WPS)", payFrom: iso(today()),
  comps: [{ c: "BASIC", amt: 0 }],
  bank: "", iban: "", bankShort: "", gosi: "", wpsExempt: false, salaryHold: false, loans: [],
  isSalesman: false, smCode: "", smType: "Counter", sellFrom: [], custMode: "All customers",
  priceList: "Retail", maxDisc: 0, commScheme: "", targets: [],
  leavePolicy: (lk.leavePolicies || [])[0] || "", annualLeave: 21, ticket: "None",
  shift: (lk.shifts || [])[0] || "", weekOff: "Friday", bioId: "",
  leaves: defaultLeaves(lk),
  assets: [],
  userGuid: "", userName: "", posLogin: false, till: "", limDisc: 0, limCn: 0, limPo: 0,
  createdOn: "", createdBy: "",
  history: [],
});

const s = (v) => (v == null ? "" : String(v));
const n = (v) => (v == null || v === "" ? 0 : +v || 0);
const guidOf = (x) => (x == null ? "" : typeof x === "string" ? x : x.guid || "");

// server record (camelCase) -> the screen's draft
export const toDraft = (r, lk) => {
  if (!r) return blankEmployee(lk);
  const branches = (r.branches || [])
    .map((b) => guidOf(b.branch || b))
    .filter(Boolean);
  const primary = guidOf(r.branch) || (r.branches || []).map((b) => (b.isPrimary ? guidOf(b.branch || b) : "")).find(Boolean) || branches[0] || "";
  if (primary && branches.indexOf(primary) < 0) branches.unshift(primary);
  return {
    id: r.id || 0,
    guid: s(r.guid),
    name: s(r.name), arName: s(r.nameArabic), code: s(r.code), shortName: s(r.shortName),
    gender: s(r.gender), dob: isoOf(r.dateOfBirth), nationality: s(r.nationality),
    empType: r.employmentType || (isLocalNationality(lk, r.nationality) ? "Local" : "Expatriate"),
    marital: s(r.maritalStatus) || "Single", blood: s(r.bloodGroup),
    phone1: s(r.mobile), phone2: s(r.homeCountryPhone), email: s(r.email),
    addr1: s(r.presentAddress), homeAddr: s(r.homeAddress), accom: s(r.accommodation) || "Company provided",
    emName: s(r.emergencyName), emRel: s(r.emergencyRelation), emPhone: s(r.emergencyPhone),
    deps: (r.dependents || []).map((d) => ({
      guid: s(d.guid), n: s(d.name), rel: s(d.relation) || "Spouse", dob: isoOf(d.dateOfBirth),
      qid: s(d.idNumber), exp: isoOf(d.idExpiry), spon: d.sponsored !== false,
    })),
    branch: primary, branches,
    dept: s(r.department), desig: s(r.designation), grade: s(r.grade),
    reportsTo: guidOf(r.reportsTo), workLoc: s(r.workLocation) || "Branch",
    doj: isoOf(r.dateOfJoining), contract: s(r.contractType) || "Unlimited", contractEnd: isoOf(r.contractEnd),
    probation: r.probationMonths == null ? 6 : n(r.probationMonths),
    status: s(r.status) || "Active", dol: isoOf(r.dateOfLeaving), leaveReason: s(r.leavingReason),
    docs: (r.documents || []).map((d) => ({
      guid: s(d.guid), type: s(d.documentType), no: s(d.number), issue: isoOf(d.issueDate),
      expiry: isoOf(d.expiryDate), stage: s(d.renewalStage) || "Not started", remarks: s(d.remarks),
      files: (d.files || []).map((f) => ({
        guid: s(f.guid), n: s(f.fileName), sz: n(f.sizeKb), on: isoOf(f.uploadedOn), url: s(f.url),
      })),
    })),
    payFreq: s(r.payFrequency) || "Monthly", payMode: s(r.paymentMode) || "Bank transfer (WPS)",
    payFrom: isoOf(r.payEffectiveFrom),
    comps: (r.salaryComponents || []).map((c) => ({ c: s(c.componentCode), amt: n(c.amount) })),
    bank: s(r.bankName), iban: s(r.iban), bankShort: s(r.bankShortName), gosi: s(r.socialInsuranceNo),
    wpsExempt: !!r.wpsExempt, salaryHold: !!r.salaryHold,
    loans: (r.loans || []).map((l) => ({
      guid: s(l.guid), on: isoOf(l.date), t: s(l.loanType) || "Salary advance",
      amt: n(l.principal), inst: n(l.instalment), out: n(l.outstanding),
    })),
    isSalesman: !!r.isSalesman, smCode: s(r.salesmanCode), smType: s(r.salesmanType) || "Counter",
    sellFrom: (r.sellFrom || []).map(guidOf).filter(Boolean),
    custMode: s(r.customerScope) || "All customers", priceList: s(r.priceList) || "Retail",
    maxDisc: n(r.maxDiscountPercent), commScheme: s(r.commissionScheme),
    targets: (r.targets || []).map((t) => ({
      guid: s(t.guid), period: s(t.period), ptype: s(t.periodType) || "Month",
      measure: s(t.measure) || "Net sales", target: n(t.target), achieved: n(t.achieved),
    })),
    leavePolicy: s(r.leavePolicy), annualLeave: r.annualLeaveDays == null ? 21 : n(r.annualLeaveDays),
    ticket: s(r.airTicket) || "None", shift: s(r.shift), weekOff: s(r.weeklyOff) || "Friday",
    bioId: s(r.biometricId),
    leaves: (r.leaveBalances || []).length
      ? r.leaveBalances.map((l) => ({ t: s(l.leaveType), opening: n(l.opening), accrued: n(l.accrued), taken: n(l.taken) }))
      : defaultLeaves(lk),
    assets: (r.assets || []).map((a) => ({
      guid: s(a.guid), t: s(a.assetType), sn: s(a.serialNo), on: isoOf(a.issuedOn),
      cond: s(a.condition) || "Good", back: isoOf(a.returnedOn),
    })),
    userGuid: guidOf(r.user) || s(r.userGuid), userName: s(r.userName), posLogin: !!r.posLogin, till: s(r.till),
    limDisc: n(r.discountApprovalLimit), limCn: n(r.creditNoteApprovalLimit), limPo: n(r.purchaseOrderApprovalLimit),
    createdOn: isoOf(r.createdDate), createdBy: s(r.createdUserName),
    history: (r.history || []).map((h) => ({ t: s(h.action), s: s(h.detail), d: isoOf(h.date), by: s(h.userName) })),
  };
};

const dateOrNull = (v) => (v ? v : null);

// the screen's draft -> the Save / Update body (PascalCase)
export const toPayload = (d, { branch, userId, isNew }) => ({
  id: isNew ? 0 : d.id || 0,
  guid: isNew ? "" : d.guid || "",
  Name: d.name.trim(),
  NameArabic: d.arName,
  Code: d.code.trim(),
  ShortName: d.shortName,
  Gender: d.gender,
  DateOfBirth: dateOrNull(d.dob),
  Nationality: d.nationality,
  EmploymentType: d.empType,
  MaritalStatus: d.marital,
  BloodGroup: d.blood,
  Mobile: d.phone1,
  HomeCountryPhone: d.phone2,
  Email: d.email,
  PresentAddress: d.addr1,
  HomeAddress: d.homeAddr,
  Accommodation: d.accom,
  EmergencyName: d.emName,
  EmergencyRelation: d.emRel,
  EmergencyPhone: d.emPhone,
  Dependents: (d.deps || []).map((x) => ({
    guid: x.guid || "", Name: x.n, Relation: x.rel, DateOfBirth: dateOrNull(x.dob),
    IdNumber: x.qid, IdExpiry: dateOrNull(x.exp), Sponsored: x.spon !== false,
  })),
  PrimaryBranchGuid: d.branch,
  BranchGuids: d.branches,
  Department: d.dept,
  Designation: d.desig,
  Grade: d.grade,
  ReportsToGuid: d.reportsTo || null,
  WorkLocation: d.workLoc,
  DateOfJoining: dateOrNull(d.doj),
  ContractType: d.contract,
  ContractEnd: dateOrNull(d.contractEnd),
  ProbationMonths: n(d.probation),
  Status: d.status,
  DateOfLeaving: dateOrNull(d.dol),
  LeavingReason: d.leaveReason,
  Documents: (d.docs || []).map((x) => ({
    guid: x.guid || "", DocumentType: x.type, Number: x.no, IssueDate: dateOrNull(x.issue),
    ExpiryDate: dateOrNull(x.expiry), RenewalStage: x.stage, Remarks: x.remarks || "",
    // files are uploaded on their own call; the save only says which to keep
    FileGuids: (x.files || []).map((f) => f.guid).filter(Boolean),
  })),
  PayFrequency: d.payFreq,
  PaymentMode: d.payMode,
  PayEffectiveFrom: dateOrNull(d.payFrom),
  SalaryComponents: (d.comps || []).map((c) => ({ ComponentCode: c.c, Amount: n(c.amt) })),
  BankName: d.bank,
  Iban: (d.iban || "").replace(/\s+/g, "").toUpperCase(),
  BankShortName: d.bankShort,
  SocialInsuranceNo: d.gosi,
  WpsExempt: !!d.wpsExempt,
  SalaryHold: !!d.salaryHold,
  Loans: (d.loans || []).map((l) => ({
    guid: l.guid || "", Date: dateOrNull(l.on), LoanType: l.t, Principal: n(l.amt),
    Instalment: n(l.inst), Outstanding: n(l.out),
  })),
  IsSalesman: !!d.isSalesman,
  SalesmanCode: d.isSalesman ? d.smCode : "",
  SalesmanType: d.smType,
  CustomerScope: d.custMode,
  PriceList: d.priceList,
  MaxDiscountPercent: n(d.maxDisc),
  CommissionScheme: d.commScheme,
  SellFromGuids: d.isSalesman ? d.sellFrom : [],
  Targets: (d.targets || []).map((t) => ({
    guid: t.guid || "", Period: t.period, PeriodType: t.ptype, Measure: t.measure, Target: n(t.target),
  })),
  LeavePolicy: d.leavePolicy,
  AnnualLeaveDays: n(d.annualLeave),
  AirTicket: d.ticket,
  Shift: d.shift,
  WeeklyOff: d.weekOff,
  BiometricId: d.bioId,
  LeaveOpening: (d.leaves || []).map((l) => ({ LeaveType: l.t, Opening: n(l.opening) })),
  Assets: (d.assets || []).map((a) => ({
    guid: a.guid || "", AssetType: a.t, SerialNo: a.sn, IssuedOn: dateOrNull(a.on),
    Condition: a.cond, ReturnedOn: dateOrNull(a.back),
  })),
  UserGuid: d.userGuid || null,
  PosLogin: !!d.posLogin,
  Till: d.till,
  DiscountApprovalLimit: n(d.limDisc),
  CreditNoteApprovalLimit: n(d.limCn),
  PurchaseOrderApprovalLimit: n(d.limPo),
  Branch: branch,
  createdUser: `{${userId?.id}}`,
  updatedUser: `{${userId?.id}}`,
});

/* ------------------------------------------------- documents */

export const RANK = { expired: 0, critical: 1, warning: 2, watch: 3, valid: 4 };

export const docStatus = (d, lk) => {
  const t = lk.docBy[d.type];
  if (!t || !t.expires) return { k: "valid", lbl: "No expiry", days: null };
  if (!d.expiry) return { k: "watch", lbl: "No date yet", days: null };
  const left = daysTo(d.expiry);
  if (left < 0) return { k: "expired", lbl: "Expired " + Math.abs(left) + "d ago", days: left };
  if (left <= 15) return { k: "critical", lbl: left + " days left", days: left };
  if (left <= 30) return { k: "warning", lbl: left + " days left", days: left };
  if (left <= 90) return { k: "watch", lbl: left + " days left", days: left };
  return { k: "valid", lbl: "Valid", days: left };
};

export const worstDoc = (e, lk) => {
  let best = null;
  (e.docs || []).forEach((d) => {
    const st = docStatus(d, lk);
    if (
      !best ||
      RANK[st.k] < RANK[best.k] ||
      (RANK[st.k] === RANK[best.k] && st.days !== null && best.days !== null && st.days < best.days)
    )
      best = st;
  });
  return best || { k: "valid", lbl: "—", days: null };
};

const appliesTo = (t, local) => t.applies === "both" || (t.applies === "local") === local;

export const missingDocs = (e, lk) => {
  const local = e.empType === "Local";
  return lk.documentTypes.filter(
    (t) => t.mandatory && appliesTo(t, local) && !(e.docs || []).some((d) => d.type === t.code)
  );
};

// add the papers this person must have, drop blank rows that no longer apply
export const seedDocs = (docs, empType, lk) => {
  const local = empType === "Local";
  const out = (docs || []).filter((d) => {
    const t = lk.docBy[d.type];
    if (!t || appliesTo(t, local)) return true;
    return !!d.no; // keep anything already filled in
  });
  lk.documentTypes.forEach((t) => {
    if (!t.mandatory || !appliesTo(t, local)) return;
    if (out.some((d) => d.type === t.code)) return;
    out.push({ guid: "", type: t.code, no: "", issue: "", expiry: "", stage: "Not started", files: [], remarks: "" });
  });
  return out;
};

export const sortDocs = (docs, lk) =>
  (docs || [])
    .map((d, i) => ({ d, i, st: docStatus(d, lk) }))
    .sort((a, b) =>
      RANK[a.st.k] !== RANK[b.st.k]
        ? RANK[a.st.k] - RANK[b.st.k]
        : (a.st.days === null ? 1e9 : a.st.days) - (b.st.days === null ? 1e9 : b.st.days)
    );

/* ------------------------------------------------- pay */

export const payOf = (e, lk) => {
  let earn = 0;
  let ded = 0;
  let cost = 0;
  let basic = 0;
  let gratBase = 0;
  (e.comps || []).forEach((x) => {
    const c = lk.compBy[x.c];
    if (!c) return;
    const amt = +x.amt || 0;
    if (c.kind === "Earning") {
      earn += amt;
      if (c.gratuity) gratBase += amt;
      if (x.c === "BASIC") basic = amt;
    } else if (c.kind === "Deduction") ded += amt;
    else cost += amt;
  });
  return { gross: earn, ded, net: earn - ded, ctc: earn + cost, basic, gratBase };
};

// Qatar Labour Law art. 54 minimum: three weeks' basic wage per year of
// service, after one year. Indicative only — the server's figure wins when it
// sends `gratuityAccrued`.
export const gratuity = (e, lk) => {
  const doj = parseISO(e.doj);
  if (!doj) return { years: 0, amount: 0, note: "" };
  const end = parseISO(e.dol) || today();
  const yrs = Math.max(0, yearsBetween(doj, end));
  const p = payOf(e, lk);
  const base = p.gratBase || p.basic;
  if (yrs < 1) return { years: yrs, amount: 0, note: "Under one year — no entitlement yet" };
  return { years: yrs, amount: (base / 30) * 21 * yrs, note: "3 weeks' basic per year of service" };
};

/* ------------------------------------------------- probation */

export const probationEnd = (e) => {
  const doj = parseISO(e.doj);
  if (!doj || !e.probation) return null;
  const d = new Date(doj.getTime());
  d.setMonth(d.getMonth() + +e.probation);
  return d;
};

/* ------------------------------------------------- IBAN */

// ISO 13616 mod-97 check. Empty is "not checked", not "bad".
export const ibanValid = (raw) => {
  const v = String(raw || "").replace(/\s+/g, "").toUpperCase();
  if (!v) return null;
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(v)) return false;
  const moved = v.slice(4) + v.slice(0, 4);
  let rem = 0;
  for (const ch of moved) {
    const code = ch >= "A" && ch <= "Z" ? String(ch.charCodeAt(0) - 55) : ch;
    for (const dg of code) rem = (rem * 10 + +dg) % 97;
  }
  return rem === 1;
};
